// The billing money model — ONE definition, shared by every page that shows money.
//
// These helpers were private to src/pages/Treatments.jsx until 2026-08-12. They
// are extracted VERBATIM (behaviour-identical) so that /treatments and the
// per-patient การรักษา ledger cannot drift into computing clinic revenue two
// different ways. They carry corrections that were expensive to find — read the
// comments before "simplifying" any of them.
//
// ⚠️ There are two OTHER copies in the app, in src/pages/reports/RevenueReport.jsx
// and src/pages/reports/Reimbursement.jsx, and they DISAGREE with this one:
//   • RevenueReport  bPrice = num(b.price ?? b.priceClinic ?? 0)   (vs unit_price here)
//   • Reimbursement  bTotal is 2-tier and returns 0 for priceActualTotal === ""
//   • Reimbursement  bDoctor / bAdvance drop the snake_case fallbacks
//   • Reimbursement  stripStars strips only leading/trailing '*', not all
// They are deliberately NOT repointed here: both render published revenue and
// reimbursement figures, and switching their arithmetic in the same commit that
// extracts this module would move numbers the clinic reconciles against. Migrate
// them separately, one at a time, with the figures checked before and after.
//
// Money model (from project.md §Treatments page — corrected after user review):
//   collection side:  รวม (priceXunit = price × N) + เบิก (priceadvance)
//                     ["รวม" was called เก็บเอง until 2026-08-13]
//   split side:       คลินิก (priceActualClinic) + หมอ (priceDoctor) = รวม (priceActualTotal)
// The SPLIT identity reconciles 100% and is the one this module relies on. The
// collection side does NOT universally sum to รวม: direct-claim reversal lines
// book a NEGATIVE priceXunit against priceActualTotal = priceadvance. That is
// expected, not a bug.

import { apiGet } from './api.js'
import { asList } from './chart-helpers.js'

/* -------------------------------------------------------------------------- */
/* scalars                                                                    */
/* -------------------------------------------------------------------------- */

export const num = (v) => { const n = Number(String(v ?? '').replace(/[, ฿]/g, '')); return isNaN(n) ? 0 : n }
export const stripStars = (s) => String(s ?? '').replace(/\*/g, '').trim()

/* -------------------------------------------------------------------------- */
/* per-billing-line accessors — defensive (camel/snake variants)              */
/* -------------------------------------------------------------------------- */

export const bActualClinic = (b) => num(b.priceActualClinic ?? b.priceClinic ?? 0)
// The NOMINAL clinic share — ค่ารักษา − DF, the same figure the billing drawer's
// Clinic box shows. The per-patient ledger's คลินิก column uses this (owner's
// call, 2026-10-26: คลินิก/หมอ/%หมอ must read exactly as in the drawer; only
// รวมเบิก includes the claim). Revenue pages keep bActualClinic.
export const bClinic = (b) => (b.priceClinic != null && String(b.priceClinic).trim() !== ''
  ? num(b.priceClinic)
  : bXunit(b) - bDoctor(b))
export const bDoctor = (b) => num(b.priceDoctor ?? b.pricedoctor ?? b.price_doctor ?? 0)
export const bUnit = (b) => num(b.N ?? b.unit ?? b.quantity ?? b.qty ?? 1) || 1  // `N` is Xano's qty field
export const bPrice = (b) => num(b.price ?? b.unit_price ?? 0)
// เก็บจากคนไข้ (out-of-pocket) vs เบิก (advance-pay claim: ปกส./ประกัน/…)
export const bXunit = (b) => (b.priceXunit != null ? num(b.priceXunit) : bPrice(b) * bUnit(b))
export const bAdvance = (b) => num(b.priceadvance ?? b.priceAdvance ?? b.price_advance ?? 0)
export const bAdvancePayer = (b) => String(b.advancePay ?? b.advancepay ?? b.advance_pay ?? '').trim()

export function bTotal(b) {
  const t = b.priceActualTotal ?? b.priceactualtotal ?? b.price_actual_total
  if (t != null && String(t).trim() !== '') return num(t)
  const split = bActualClinic(b) + bDoctor(b)
  if (split) return split
  return bXunit(b) + bAdvance(b)
}

export const bPctDoctor = (b) => { const v = b.percentDoctor; return v == null || v === '' ? null : num(v) }
export const bDetailNote = (b) => String(b.detail ?? '').trim()
export const bFixedDF = (b) => b.fixed_DF === true || b.fixedDF === true

export function bName(b) {
  const bt = b.billing_type ?? b.billingType
  return (bt && (bt.name || bt.detail || bt.title)) || b.name || b.detail || b.title || b.note || 'รายการ'
}

export const bTypeTag = (b) => (b.billing_type ?? b.billingType)?.type || ''

// Per-visit overhead, not treatment: the sterilisation fee and the two lines
// autofeeBilling maintains (1.5% card fee, 1% insurance fee). BillingTable pins them
// last; the drawer's treatment summary leaves them out.
export const OVERHEAD_TYPE_IDS = [238, 212, 340]
export const isOverheadLine = (b) => OVERHEAD_TYPE_IDS.includes(Number(b?.billing_type_id))

export function bPromoName(b) {
  const p = b.promotion
  if (p && typeof p === 'object') return String(p.name || p.detail || p.title || '').trim()
  if (typeof p === 'string') return p.trim()
  return ''
}

export const bHasPromo = (b) => num(b.promotion_id) > 0 || num(b.promotion_count) > 0 || !!bPromoName(b)

// A billing line should reconcile as: priceActualClinic + priceDoctor = priceActualTotal.
// Flag only genuine breaks (>฿1) — advance-pay lines reconcile under this identity
// (unlike the nominal priceClinic, which excludes the claimed-back portion).
export function bMismatch(b) {
  return Math.abs(bTotal(b) - (bActualClinic(b) + bDoctor(b))) > 1
}

// Xano json/list columns are not guaranteed to arrive as arrays — an empty one
// can come back as {} or "". Everything that iterates a nested list must go
// through this or a render crashes the whole SPA (there is no ErrorBoundary).
export const asArray = (v) => (Array.isArray(v) ? v : [])

/* ── claim (ตามเบิก) state ──────────────────────────────────────────────────
 * `advance_status` has FOUR values as of 2026-08-24. The two originals mean what
 * they always did; the two new ones exist because insurers do things ปกส. never did.
 *
 *   ""          no claim / not applicable. ⚠️ NOT pending — the default on every cash
 *               line in the table, and treating it as pending renders the whole ledger
 *               as money owed. 4,179 real AXA+Generali lines also sit here simply
 *               because nobody has ever reconciled them.
 *   "pending"   ค้างรับ — we expect this money. ⚠️ INCLUDES a claim the insurer has
 *               refused but that we are still arguing about. A refusal is an opening
 *               position, not an outcome.
 *   "partial"   the insurer paid LESS than we claimed and the matter is closed at that
 *               amount. The shortfall is NOT collectable from the payer.
 *   "rejected"  we have accepted we will never be paid. ⚠️ A HUMAN DECISION ONLY —
 *               no importer, no automation and no insurer's own wording may ever
 *               produce this value. It is the clinic giving up, not the payer refusing.
 *
 * ⚠️ THE WHITELIST BELOW IS LOAD-BEARING. Before it knew about the new values it
 * collapsed them to "" — i.e. "not a claim at all" — so a part-paid line would have
 * VANISHED from claim tracking rather than shown as part-paid. Any future value must be
 * added here in the same change that adds it to the Xano enum.
 */
const CLAIM_STATES = new Set(['pending', 'received', 'partial', 'rejected'])

export function bClaimStatus(b) {
  const s = String(b?.advance_status ?? '').trim().toLowerCase()  // Reimbursement.jsx lowercases too
  return CLAIM_STATES.has(s) ? s : ''
}

// How much the payer actually sent for this line. Only meaningful on `partial`;
// `received` is by definition the full `priceadvance`.
export const bClaimReceived = (b) => {
  const s = bClaimStatus(b)
  if (s === 'received') return bAdvance(b)
  if (s !== 'partial') return 0
  const v = b?.advance_received_amount
  return v == null || v === '' ? 0 : num(v)
}

// ⚠️ MONEY STILL OWED BY THE PAYER, and the only figure ค้างรับ may ever be summed from.
// `rejected` is written off — counting it keeps dead money on the books forever, which
// is exactly what a write-off is supposed to stop. `partial` owes only the shortfall,
// and a shortfall is normally 0 because the settlement closed the line.
export function bClaimOwed(b) {
  const s = bClaimStatus(b)
  if (s === 'pending') return bAdvance(b)
  if (s === 'partial') return 0
  return 0
}

// The shortfall on a part payment — reported, but NOT owed by the payer. Whether the
// patient owes it is a business decision that lives outside this file (§12.2).
export const bClaimShortfall = (b) =>
  (bClaimStatus(b) === 'partial' ? Math.max(0, bAdvance(b) - bClaimReceived(b)) : 0)

// Still chasing this one. `partial` is closed at the settled amount, so it is NOT open.
export const bClaimOpen = (b) => bAdvance(b) !== 0 && bClaimStatus(b) === 'pending'

// A terminal state no automation may set or overwrite.
export const bClaimFinal = (b) => {
  const s = bClaimStatus(b)
  return s === 'rejected' || s === 'partial'
}

/* ── missing รหัสเคลม (Ram, 2026-10-21) ─────────────────────────────────────
 * A line billed to a payer (advancePay set, priceadvance ≠ 0) must carry its claim
 * code in `claimRef` — without it the money cannot be matched when the insurer pays.
 * EXCEPT สปสช. and ปกส.: those are claimed by batch/portal, not per-line codes.
 * Payer matching is tolerant (case, spaces, dots, dashes), like reimbursementPayers.
 */
export const bClaimRef = (b) => String(b?.claimRef ?? b?.claim_ref ?? '').trim()
const payerKey = (s) => String(s || '').toLowerCase().replace(/[\s.\-]/g, '')
const CLAIM_REF_EXEMPT = new Set(['สปสช.', 'ปกส.'].map(payerKey))
export const isClaimRefExempt = (payer) => CLAIM_REF_EXEMPT.has(payerKey(payer))
export function bMissingClaimRef(b) {
  const payer = bAdvancePayer(b)
  return !!payer && bAdvance(b) !== 0 && !bClaimRef(b) && !isClaimRefExempt(payer)
}

/* -------------------------------------------------------------------------- */
/* the day lock — SCREEN COPY ONLY                                            */
/* -------------------------------------------------------------------------- */

// A billing line may be added / edited / voided only on its treatment's own
// Bangkok day; any other day is manager · GM · admin. These two helpers exist so
// the editor can grey the controls and say why, INSTEAD of letting someone fill
// in a form and meet a 403 at the end.
//
// ⚠️ This is the convenience copy. The enforcing copy is
// functions/api/billing/_guard.js, and it is deliberately duplicated rather
// than shared: a Pages Function and the bundle cannot import the same module,
// and the alternative — no client-side lock at all — is a worse experience.
// Change one, change the other; bkkDay() is character-for-character the same
// function in both files, including failing closed on an unreadable date.
const BKK_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Bangkok', year: 'numeric', month: '2-digit', day: '2-digit',
})
export const bkkToday = () => BKK_FMT.format(new Date())

export function bkkDay(v) {
  if (v == null || v === '') return ''
  if (typeof v === 'number') return BKK_FMT.format(new Date(v))
  const s = String(v).trim()
  if (/^\d+$/.test(s)) return BKK_FMT.format(new Date(Number(s)))
  // A bare YYYY-MM-DD is already a calendar day — passing it through a timezone
  // would shift it. Anything richer must be converted.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const d = new Date(s)
  return isNaN(d) ? '' : BKK_FMT.format(d)
}

// Pass src/lib/role.js#canEditPastBilling(me) as `mayEditPast` — NOT canManage(),
// which also admits stockmanager and would light up a button the proxy refuses.
export const dayLocked = (txDate, mayEditPast) => !mayEditPast && bkkDay(txDate) !== bkkToday()

// Dropdown order for วิธีชำระเงิน. The payment_method table's own order is
// insertion order, which puts โอนเงิน — 60% of all visits — third, and floats two
// never-used methods above เบิกย้อนหลัง. Measured over 2026 (55,079 treatments,
// 45,890 with a method set):
//   โอนเงิน 27,315 · บัตรเครดิต 7,667 · เบิกย้อนหลัง 6,538 · เงินสด 4,246 ·
//   บัตรเดบิต 70 · GB Primepay 54 · FB PAY 0 · True Money 0
// so the list leads with what the front desk actually picks. Re-measure before
// trusting this order years from now — it is a snapshot, not a rule.
const PAY_ORDER = ['โอนเงิน', 'บัตรเครดิต', 'เบิกย้อนหลัง', 'เงินสด', 'บัตรเดบิต', 'GB Primepay', 'FB PAY', 'True Money']

// A method added in Xano later is NOT dropped — it sorts after the known ones,
// alphabetically, so it still appears in every picker.
// Catalogue order for a billing-type picker: bucket, then live-before-retired,
// then name. SearchableSelect draws a header wherever `group` CHANGES down the
// list — it does not sort — so an unsorted list fragments one type into several
// headers. Extracted from lib/promotion.js 2026-08-14 when BillingLineEditor
// turned out to be passing `group` without ever sorting by it.
//
// A blank bucket sorts LAST: an unlabelled block at the top reads as the most
// important one.
export function sortBillingTypes(list) {
  const type = (b) => String(b?.type || '').trim()
  const name = (b) => String(b?.name || b?.nameEng || b?.receipt_type || `#${b?.id}`)
  return [...(list || [])].sort((a, b) =>
    (type(a) ? 0 : 1) - (type(b) ? 0 : 1)
    || type(a).localeCompare(type(b), 'th')
    || (a.active === false ? 1 : 0) - (b.active === false ? 1 : 0)
    || name(a).localeCompare(name(b), 'th'))
}

export function sortPayMethods(names) {
  const rank = (n) => {
    const i = PAY_ORDER.indexOf(String(n ?? '').trim())
    return i === -1 ? PAY_ORDER.length : i
  }
  return [...names].sort((a, b) => rank(a) - rank(b) || String(a).localeCompare(String(b), 'th'))
}

// A soft-deleted line. Xano's DELETE /billing/{id} only flips `active`, and
// `GET /billing` still returns the row — so anything reading that endpoint must
// filter. (The per-patient treatment feed does NOT: its billing_treatment addon
// already filters active == true upstream.)
export const bIsActive = (b) => b?.active !== false

// `priceDoctor === 0` is NOT "no fee" — df_doctor/gen3 filters
// `priceDoctor != 0 && != ""` and takes Σ priceXunit from that SAME filtered
// set, so a zero-DF line drops its PATIENT REVENUE out of that doctor's month
// too. A null/'' DF keeps the revenue. Surfaced so the ledger can badge it.
export const bZeroDF = (b) => num(b.priceDoctor) === 0 && b.priceDoctor != null && String(b.priceDoctor) !== ''

/* -------------------------------------------------------------------------- */
/* aggregates                                                                 */
/* -------------------------------------------------------------------------- */

// Sum a treatment's billing lines. Shape: { total, clinic, doctor, patient, advance }.
export function sumTx(tx) {
  return sumLines(asArray(tx?.billing))
}

// Same arithmetic over a bare array of lines — for table footers and lifetime
// roll-ups, so a summary card and the footer beneath it cannot disagree.
export function sumLines(lines) {
  let total = 0, clinic = 0, doctor = 0, patient = 0, advance = 0
  for (const b of asArray(lines)) {
    total += bTotal(b); clinic += bActualClinic(b); doctor += bDoctor(b)
    patient += bXunit(b); advance += bAdvance(b)
  }
  return { total, clinic, doctor, patient, advance }
}

/* -------------------------------------------------------------------------- */
/* treatment-level                                                            */
/* -------------------------------------------------------------------------- */

// Xano's `doctor.code` IS the display name in this workspace (e.g. "เอก ธนิตย์"),
// not a short code.
export const doctorName = (tx) => (tx.doctor?.code || tx.doctor?.name || tx.doctor?.full_name || '').trim()

// treatment's payment method → { key, name (display), color }.
export function paymentOf(t, pmById) {
  const color = t.payment_color || null
  const disp = t.payment_display
  const raw = t.paymentMethod ?? t.paymentmethod ?? t.payment_method ?? t.payment
  let name = disp && String(disp).trim() ? String(disp).trim() : ''
  if (!name && raw != null) {
    name = typeof raw === 'object'
      ? String(raw.name || raw.label || raw.type || raw.value || '').trim()
      : String(raw).trim()
  }
  if (!name) { const s = String(raw ?? '').trim(); if (pmById.has(s)) name = pmById.get(s) }
  if (!name) return { key: '__none__', name: 'ไม่ระบุ', color: null }
  return { key: name, name, color }
}

// id -> display name, for paymentOf's id-lookup path.
//
// Must match /treatments exactly. A narrower fallback chain here would leave the
// map empty for any row whose label lives in another column; paymentOf would
// then return ไม่ระบุ and the ledger would fire its red "no payment method"
// risk badge on a visit that /treatments renders correctly.
export function buildPmIndex(list) {
  const m = new Map()
  // asList, NOT asArray: /api/payment-methods can return the Xano envelope
  // ({items:[…]}), and asArray would silently yield an empty map — which
  // empties the payment-method <select> on /treatments.
  for (const p of asList(list)) {
    const key = String(p.id ?? p.value)
    const name = String(p.display || p.name || p.paymentMethod || p.method || p.label || p.detail || p.title || p.id || '')
    if (key) m.set(key, name)
  }
  return m
}

export const isNoPay = (key) => key === '__none__'

// billing-line sort comparators (keyed by the sortable column)
export const BILLING_SORTS = {
  patient: { label: 'รวม', fn: (a, b) => bXunit(b) - bXunit(a) },       // priceXunit desc (default)
  total: { label: 'รวมเบิก', fn: (a, b) => bTotal(b) - bTotal(a) },            // priceActualTotal desc
  type: { label: 'รายการ', fn: (a, b) => (bTypeTag(a) || bName(a)).localeCompare(bTypeTag(b) || bName(b), 'th') },
}

/* -------------------------------------------------------------------------- */
/* per-patient treatment feed                                                 */
/* -------------------------------------------------------------------------- */

const TX_PER_PAGE = 200   // was 50 — see fetchAllTreatmentsMeta
const TX_MAX_PAGES = 20

/**
 * Walk `GET /api/treatments?type=patient` until the patient's whole history is
 * loaded. Returns `{ items, truncated }`.
 *
 * `truncated` matters: at 200 × 20 the walk stops at 4000 rows and, before this
 * module, at 50 × 20 it stopped at 1000 — SILENTLY. On a clinical list a dropped
 * row is a dropped row, but the การรักษา ledger presents a LIFETIME TOTAL as
 * authoritative, and a silently truncated total is a wrong number, not a missing
 * one. Callers that show money must surface `truncated`.
 *
 * Rows are deduped by id: the upstream query LEFT JOINs `chart` on a non-unique
 * index, so a treatment with two chart rows would otherwise be counted twice.
 */
export async function fetchAllTreatmentsMeta(patientId, { perPage = TX_PER_PAGE, maxPages = TX_MAX_PAGES } = {}) {
  const byId = new Map()
  const loose = []
  let page = 1
  let truncated = false

  for (let i = 0; i < maxPages; i++) {
    const res = await apiGet('/api/treatments', { patient_id: patientId, type: 'patient', per_page: perPage, page })
    const batch = asList(res)
    for (const t of batch) {
      const id = t?.id ?? t?.treatment_id
      if (id == null) { loose.push(t); continue }
      if (!byId.has(String(id))) byId.set(String(id), t)
    }
    const pageTotal = Number(res?.pageTotal || 0)
    const nextPage = res?.nextPage
    if (batch.length < perPage) break
    if (pageTotal && page >= pageTotal) break
    if (!nextPage) break
    page += 1
    // Ran out of page budget with more still to come.
    if (i === maxPages - 1) truncated = true
  }

  return { items: [...byId.values(), ...loose], truncated }
}

/** Array-only form, for the chart pages that don't render money totals. */
export async function fetchAllTreatments(patientId, opts) {
  const { items } = await fetchAllTreatmentsMeta(patientId, opts)
  return items
}

/**
 * Pull ONE treatment from the per-patient list endpoint instead of the
 * single-record endpoint, so the response shape matches what the history list
 * uses (same addons, same fields).
 *
 * Deliberately NOT implemented on top of fetchAllTreatmentsMeta: it early-exits
 * the moment the row is found, which on the app's most-opened patient page keeps
 * this a one-request lookup instead of materialising the whole history.
 */
export async function fetchTreatmentFromList(patientId, treatmentId, { perPage = TX_PER_PAGE, maxPages = 10 } = {}) {
  let page = 1
  for (let i = 0; i < maxPages; i++) {
    const res = await apiGet('/api/treatments', { patient_id: patientId, type: 'patient', per_page: perPage, page })
    const batch = asList(res)
    const found = batch.find((x) => String(x?.id ?? x?.treatment_id) === String(treatmentId))
    if (found) return found
    const pageTotal = Number(res?.pageTotal || 0)
    if (batch.length < perPage) break
    if (pageTotal && page >= pageTotal) break
    if (!res?.nextPage) break
    page += 1
  }
  return null
}

/* -------------------------------------------------------------------------- */
/* presentation constants shared by every billing table                       */
/* -------------------------------------------------------------------------- */

// รวม (priceXunit) accent. This column is emphasised everywhere because it is
// the figure staff reconcile against the cash drawer at close.
export const TEAL = '#0d9488'

// Whole-baht money formatting for billing tables.
//
// ⚠️ Must stay byte-identical to `fmt` in src/pages/statistic/statShared.jsx —
// /treatments renders the same table with that one, and a formatter that
// disagreed by a rounding mode would make the two pages show different totals
// for the same rows. Duplicated ON PURPOSE rather than imported: this module is
// in lib/ and statShared.jsx is in pages/, and nothing else in lib/ imports from
// pages/. It is one expression; the drift risk is lower than the layering cost.
// (lib/expense.js#fmtMoney is NOT a substitute — it renders 2 decimals.)
export const fmtBill = (n) => Math.round(Number(n) || 0).toLocaleString('en-US')
